import { Link } from "react-router-dom";
import { Compass, Crown, Sparkles, Zap, ShieldCheck, Globe2 } from "lucide-react";

const ABOUT_IMG = "https://images.pexels.com/photos/14725587/pexels-photo-14725587.jpeg?auto=compress&cs=tinysrgb&w=1600";

const OFFER = [
  { icon: Sparkles, title: "Curated trending picks", text: "Daily and weekly rankings pulled from the world's most active movie community." },
  { icon: Compass, title: "Effortless discovery", text: "Search, filter by genre and browse by mood without ever losing the plot." },
  { icon: Zap, title: "One click to play", text: "Every title links straight to a trusted streaming partner. No detours." },
];

const WHY = [
  { icon: Crown, title: "Premium, ad-light design", text: "A calm, cinematic interface built for people who love film." },
  { icon: ShieldCheck, title: "Trusted data", text: "Ratings, cast and trailers sourced from TMDB, refreshed constantly." },
  { icon: Globe2, title: "Made for every screen", text: "From a phone on the commute to a 4K monitor at midnight." },
];

const Feature = ({ icon: Icon, title, text }) => (
  <div className="group rounded-2xl border border-white/10 bg-[#121212] p-8 transition-colors duration-300 hover:border-[#D4AF37]/40">
    <Icon className="h-6 w-6 text-[#D4AF37] transition-transform duration-300 group-hover:-translate-y-0.5" />
    <h3 className="mt-6 font-display text-xl text-white">{title}</h3>
    <p className="mt-3 text-sm leading-relaxed text-neutral-400">{text}</p>
  </div>
);

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="relative h-[70vh] min-h-[480px] overflow-hidden">
        <img src={ABOUT_IMG} alt="Cinema screening room" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/75 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
        <div className="relative mx-auto flex h-full max-w-7xl items-end px-5 pb-20 md:px-10">
          <div className="hero-copy max-w-2xl">
            <p className="eyebrow text-[#D4AF37]">About Exclusive Streams</p>
            <h1 className="mt-4 font-display text-4xl font-bold leading-[1.05] text-white sm:text-5xl lg:text-6xl">
              The velvet rope <br /> to great cinema.
            </h1>
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-10 px-5 py-20 md:grid-cols-12 md:px-10 md:py-24">
        <div className="md:col-span-4">
          <p className="eyebrow text-[#D4AF37]">Our mission</p>
          <h2 className="mt-3 font-display text-3xl text-white">Less scrolling. More watching.</h2>
        </div>
        <div className="space-y-5 text-base leading-relaxed text-neutral-300 md:col-span-7 md:col-start-6">
          <p data-testid="about-mission">
            Exclusive Streams was founded on a simple belief: finding a great film should feel as good as watching one. We spotlight
            the movies the world is talking about today and connect you with a trusted place to stream them in a single click.
          </p>
          <p className="text-neutral-400">
            We are an independent movie discovery publisher. We don't host video; instead we partner with streaming services and
            surface the titles worth your evening, beautifully presented and always up to date.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-10 md:px-10">
        <p className="eyebrow text-[#D4AF37]">What we offer</p>
        <div className="mt-6 grid gap-6 md:grid-cols-3">{OFFER.map((f) => <Feature key={f.title} {...f} />)}</div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-20 md:px-10">
        <div className="grid gap-6 md:grid-cols-12">
          <div className="flex flex-col justify-between rounded-2xl bg-[#D4AF37] p-10 text-black md:col-span-5">
            <p className="eyebrow">Why choose us</p>
            <h2 className="mt-10 font-display text-3xl font-bold leading-tight md:text-4xl">Taste, curated. Choice, simplified.</h2>
            <Link to="/movies" data-testid="about-browse-btn" className="mt-10 inline-flex w-fit rounded-full bg-black px-7 py-3 text-sm font-semibold uppercase tracking-[0.14em] text-[#D4AF37] transition-transform duration-300 hover:scale-105">
              Browse movies
            </Link>
          </div>
          <div className="grid gap-6 md:col-span-7">{WHY.map((f) => <Feature key={f.title} {...f} />)}</div>
        </div>
      </section>
    </div>
  );
}
